import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Video,
  VideoOff,
  Mic,
  MicOff,
  Bell,
  Moon,
  Sun,
  ChevronLeft,
  ChevronUp,
  CheckCircle,
  Camera,
  Volume2,
  Settings,
  X,
  CalendarCheck,
  Users,
  AlertCircle,
  Trash2,
  Plus,
} from "lucide-react";
import Dropdown from "../components/Dropdown";
import DateTimeHeader from "../components/DateTimeHeader";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";

const PreMeetingRoom: React.FC = () => {
  const [name, setName] = useState("");
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(false);
  const [meetingName, setMeetingName] = useState("Team Meeting");
  const [meetingTime, setMeetingTime] = useState("10:00 AM - 11:00 AM");
  const [meetingHost, setMeetingHost] = useState("John Smith");
  // const [darkMode, setDarkMode] = useState(false);
  const {darkMode, toggleDarkMode} = useTheme();
  const [meetingDuration, setMeetingDuration] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
    const {user, logout} = useAuth();
  

  // Mock notifications and modal state (match home page)
  const initialNotifications = [
    {
      id: 1,
      icon: <CalendarCheck className="text-blue-400" size={18} />,
      text: "Team Standup meeting starts in 15 minutes",
    },
    {
      id: 2,
      icon: <Video className="text-green-400" size={18} />,
      text: 'Sarah invited you to join "Q4 Planning Session"',
    },
    {
      id: 3,
      icon: <Users className="text-purple-400" size={18} />,
      text: "3 participants joined your meeting room",
    },
    {
      id: 4,
      icon: <AlertCircle className="text-red-400" size={18} />,
      text: "Your account password expires in 7 days",
    },
  ];
  const [notifications, setNotifications] = useState(initialNotifications);
  const [clearedNotifications, setClearedNotifications] = useState<any[]>([]);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [notificationsTab, setNotificationsTab] = useState<
    "current" | "cleared"
  >("current");

  const handleClearNotification = (id: number) => {
    const notif = notifications.find((n) => n.id === id);
    if (notif) {
      setNotifications(notifications.filter((n) => n.id !== id));
      setClearedNotifications([notif, ...clearedNotifications]);
    }
  };

  const handleClearAll = () => {
    setClearedNotifications([...notifications, ...clearedNotifications]);
    setNotifications([]);
  };

  const handleRestoreNotification = (id: number) => {
    const notif = clearedNotifications.find((n) => n.id === id);
    if (notif) {
      setClearedNotifications(clearedNotifications.filter((n) => n.id !== id));
      setNotifications([notif, ...notifications]);
    }
  };

  // Dropdown states
  const [showMicDropdown, setShowMicDropdown] = useState(false);
  const [showCameraDropdown, setShowCameraDropdown] = useState(false);
  const [selectedMicrophone, setSelectedMicrophone] =
    useState("Default Microphone");
  const [selectedSpeaker, setSelectedSpeaker] = useState("Default Speaker");
  const [selectedCamera, setSelectedCamera] = useState("Default Camera");
  const [mirrorVideo, setMirrorVideo] = useState(false);

  // Settings Modal State
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsTab, setSettingsTab] = useState<
    "Audio" | "Video" | "Notifications" | "Profile" | "Shortcuts" | "General"
  >("Audio");
  const [enableNoiseSuppression, setEnableNoiseSuppression] = useState(true);

  // Mock audio devices (same as meeting room)
  const mockAudioDevices = {
    microphones: [
      { id: "mic1", name: "Default Microphone", isActive: true },
      { id: "mic2", name: "USB Microphone", isActive: false },
      { id: "mic3", name: "Built-in Microphone", isActive: false },
      { id: "mic4", name: "Bluetooth Headset Mic", isActive: false },
    ],
    speakers: [
      { id: "spk1", name: "Default Speaker", isActive: true },
      { id: "spk2", name: "USB Headphones", isActive: false },
      { id: "spk3", name: "Built-in Speakers", isActive: false },
      { id: "spk4", name: "Bluetooth Headset", isActive: false },
    ],
    cameras: [
      { id: "cam1", name: "Default Camera", isActive: true },
      { id: "cam2", name: "HD USB Camera", isActive: false },
      { id: "cam3", name: "Built-in Camera", isActive: false },
      { id: "cam4", name: "Virtual Camera", isActive: false },
    ],
  };

  const handleCameraSelect = (cameraName: string) => {
    setSelectedCamera(cameraName);
  };

  const handleMicrophoneSelect = (micName: string) => {
    setSelectedMicrophone(micName);
  };

  const handleSpeakerSelect = (speakerName: string) => {
    setSelectedSpeaker(speakerName);
  };

  useEffect(() => {
    // Get meeting name from URL parameters if available
    const meetingNameParam = searchParams.get("meetingName");
    if (meetingNameParam) {
      setMeetingName(decodeURIComponent(meetingNameParam));
    }

    // Get meeting time from URL parameters if available
    const meetingTimeParam = searchParams.get("meetingTime");
    if (meetingTimeParam) {
      setMeetingTime(decodeURIComponent(meetingTimeParam));
    }

    // Get meeting host from URL parameters if available
    const meetingHostParam = searchParams.get("meetingHost");
    if (meetingHostParam) {
      setMeetingHost(decodeURIComponent(meetingHostParam));
    }

    // Start duration timer
    const interval = setInterval(() => {
      setMeetingDuration((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [searchParams]);

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    }
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const handleJoinMeeting = () => {
    if (name.trim()) {
      // Store user preferences in localStorage or context
      localStorage.setItem("userName", name);
      localStorage.setItem("cameraEnabled", cameraEnabled.toString());
      localStorage.setItem("microphoneEnabled", microphoneEnabled.toString());

      // Navigate to the waiting room first
      const params = new URLSearchParams();
      params.set("meetingName", encodeURIComponent(meetingName));
      params.set("meetingHost", encodeURIComponent(meetingHost));
      navigate(`/waiting-room?${params.toString()}`);
    }
  };

  const handleGoBack = () => {
    navigate("/");
  };

  return (
    <div
      className={`min-h-screen ${
        darkMode
          ? "bg-gradient-to-br from-[#0a0a1a] via-[#1a1a2e] to-[#16213e] text-white"
          : "bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 text-gray-900"
      }`}
    >
      {/* Top Status Bar - Enhanced Design */}
      <div
        className={`sticky top-0 z-50 ${
          darkMode ? "bg-black/30" : "bg-white/30"
        } backdrop-blur-xl border-b ${
          darkMode ? "border-white/10" : "border-gray-200/30"
        }`}
      >
        <div className="flex items-center justify-between px-4 sm:px-6 py-4">
          {/* Meeting Info */}
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="flex items-center gap-3">
              <button
                onClick={handleGoBack}
                className={`p-2.5 rounded-xl transition-all duration-200 group ${
                  darkMode
                    ? "hover:bg-white/10 hover:shadow-lg"
                    : "hover:bg-gray-100 hover:shadow-md"
                }`}
              >
                <ChevronLeft
                  className={`${
                    darkMode
                      ? "text-white/70 group-hover:text-white"
                      : "text-gray-600 group-hover:text-gray-800"
                  }`}
                  size={20}
                />
              </button>
              <DateTimeHeader darkMode={darkMode} />
            </div>
          </div>

          {/* Status Indicators */}
          <div className="flex items-center gap-6">
            <button
              className={`p-2 rounded-lg transition-all duration-200 group ${
                darkMode ? "hover:bg-white/10" : "hover:bg-gray-200"
              }`}
              onClick={toggleDarkMode}
            >
              {darkMode ? (
                <Sun
                  className="text-white/70 group-hover:text-white"
                  size={22}
                />
              ) : (
                <Moon
                  className="text-gray-600 group-hover:text-gray-800"
                  size={22}
                />
              )}
            </button>
            <Dropdown
              trigger={
                <button
                  className={`relative p-2 rounded-lg transition-all duration-200 group ${
                    darkMode ? "hover:bg-white/10" : "hover:bg-gray-200"
                  }`}
                >
                  <Bell
                    className={`${
                      darkMode
                        ? "text-white/70 group-hover:text-white"
                        : "text-gray-600 group-hover:text-gray-800"
                    }`}
                    size={22}
                  />
                  {notifications.length > 0 && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-red-500 to-pink-500 rounded-full border-2 border-white"></span>
                  )}
                </button>
              }
              align="right"
              darkMode={darkMode}
            >
              <div
                className={`md:w-96 w-15 rounded-xl shadow-2xl border p-4 ${
                  darkMode
                    ? "bg-[#232336] border-white/10"
                    : "bg-white border-gray-200"
                }`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-4 pl-3">
                  <div className="flex items-center gap-2">
                    <h3
                      className={`text-lg font-bold ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Notifications
                    </h3>
                  </div>
                  {notifications.length > 0 && (
                    <span className="px-2 py-1 bg-green-600/20 text-green-600 text-xs rounded-full font-medium">
                      {notifications.length} new
                    </span>
                  )}
                </div>
                {/* Notifications List */}
                <div className="max-h-64 overflow-y-auto scrollbar-hide">
                  {notifications.length === 0 ? (
                    <div className="text-center py-8">
                      <div
                        className={`${
                          darkMode ? "bg-white/5" : "bg-gray-100"
                        } w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full`}
                      >
                        <Bell
                          size={20}
                          className={`${
                            darkMode ? "text-white/40" : "text-gray-400"
                          }`}
                        />
                      </div>
                      <p
                        className={`text-sm ${
                          darkMode ? "text-white/60" : "text-gray-600"
                        }`}
                      >
                        No notifications
                      </p>
                      <p
                        className={`text-xs mt-1 ${
                          darkMode ? "text-white/40" : "text-gray-500"
                        }`}
                      >
                        You're all caught up!
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notifications.slice(0, 4).map((n) => (
                        <div
                          key={n.id}
                          className={`group p-3 rounded-lg border transition-all duration-200 cursor-pointer ${
                            darkMode
                              ? "bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20"
                              : "bg-gray-50 border-gray-200 hover:bg-gray-100 hover:border-gray-300"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 mt-0.5">{n.icon}</div>
                            <div className="flex-1 min-w-0">
                              <p
                                className={`text-sm leading-relaxed ${
                                  darkMode ? "text-white/90" : "text-gray-900"
                                }`}
                              >
                                {n.text}
                              </p>
                              <p
                                className={`text-xs mt-1 ${
                                  darkMode ? "text-white/40" : "text-gray-500"
                                }`}
                              >
                                Just now
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Footer */}
                {notifications.length > 0 && (
                  <div
                    className={`mt-4 pt-3 border-t ${
                      darkMode ? "border-white/10" : "border-gray-200"
                    }`}
                  >
                    <button
                      className="w-full text-center text-sm font-medium py-2 px-4 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 hover:scale-105 text-white transition-all duration-200 shadow-lg"
                      onClick={() => setShowNotificationsModal(true)}
                    >
                      View all notifications
                    </button>
                  </div>
                )}
              </div>
            </Dropdown>
            <Dropdown
              trigger={
                <div
                  className={`flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2 rounded-xl cursor-pointer transition-all duration-200 ${
                    darkMode ? "hover:bg-white/10" : "hover:bg-gray-100"
                  }`}
                >
                  <img
                    src="https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"
                    alt="profile"
                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white/20 flex-shrink-0"
                  />
                  <div className="hidden sm:block text-left min-w-0 flex-1">
                    <div
                      className={`text-xs sm:text-sm font-medium truncate ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {user ? user?.first_name + " " + user?.last_name : "Guest User"}
                    </div>
                    <div
                      className={`text-xs ${
                        darkMode ? "text-white/60" : "text-gray-500"
                      }`}
                    >
                      Online
                    </div>
                  </div>
                  <svg
                    className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${
                      darkMode ? "text-white/40" : "text-gray-400"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              }
              align="right"
              darkMode={darkMode}
            >
              <div className="w-full max-w-xs sm:max-w-sm">
                {/* Header */}
                <div className="p-4 sm:p-6 pb-3 sm:pb-4">
                  <div className="flex items-center gap-3 sm:gap-4">
                    <div className="relative flex-shrink-0">
                      <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full p-0.5 bg-gradient-to-r from-pink-500 to-red-500">
                        <div className="w-full h-full rounded-full overflow-hidden">
                          <img
                            src="https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"
                            alt="avatar"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3
                        className={`text-base sm:text-lg font-bold truncate ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {user ? user?.first_name + " " + user?.last_name : "Guest User"}
                      </h3>
                      <p
                        className={`text-xs sm:text-sm truncate ${
                          darkMode ? "text-white/60" : "text-gray-600"
                        }`}
                      >
                        {user ? user.email : "guest@example.com"}
                      </p>
                      <div
                        className={`text-xs sm:text-sm mt-1 ${
                          darkMode ? "text-white/70" : "text-gray-600"
                        }`}
                      >
                        Software Engineer
                      </div>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="px-2 sm:px-3 pb-3">
                  <div className="space-y-0.5 sm:space-y-1">
                    <button
                      onClick={() => navigate("/settings")}
                      className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-left transition-all duration-200 group ${
                        darkMode ? "hover:bg-white/10" : "hover:bg-gray-50"
                      }`}
                    >
                      <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-600 to-red-500 shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                        <svg
                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-xs sm:text-sm font-semibold truncate ${
                            darkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          Settings
                        </div>
                        <div
                          className={`text-xs truncate ${
                            darkMode ? "text-white/50" : "text-gray-500"
                          }`}
                        >
                          Preferences & privacy
                        </div>
                      </div>
                      <svg
                        className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${
                          darkMode ? "text-white/30" : "text-gray-400"
                        } group-hover:scale-110 transition-transform duration-200`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>

                    <button
                      onClick={() => navigate("/help")}
                      className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-left transition-all duration-200 group ${
                        darkMode ? "hover:bg-white/10" : "hover:bg-gray-50"
                      }`}
                    >
                      <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-600 to-red-500 shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                        <svg
                          className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                          />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-xs sm:text-sm font-semibold truncate ${
                            darkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          Help & Support
                        </div>
                        <div
                          className={`text-xs truncate ${
                            darkMode ? "text-white/50" : "text-gray-500"
                          }`}
                        >
                          Get assistance
                        </div>
                      </div>
                      <svg
                        className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${
                          darkMode ? "text-white/30" : "text-gray-400"
                        } group-hover:scale-110 transition-transform duration-200`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Divider */}
                  <div
                    className={`my-2 sm:my-3 mx-2 sm:mx-3 h-px ${
                      darkMode
                        ? "bg-gradient-to-r from-transparent via-white/10 to-transparent"
                        : "bg-gradient-to-r from-transparent via-gray-200 to-transparent"
                    }`}
                  ></div>

                  {/* Sign Out Button */}
                  <button
                  onClick={logout}
                    className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-left transition-all duration-200 group ${
                      darkMode
                        ? "hover:bg-red-500/10 hover:text-red-400"
                        : "hover:bg-red-50 hover:text-red-600"
                    }`}
                  >
                    <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-600 to-red-500 shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                      <svg
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs sm:text-sm font-semibold truncate">
                        Sign Out
                      </div>
                      <div
                        className={`text-xs truncate ${
                          darkMode ? "text-white/50" : "text-gray-500"
                        }`}
                      >
                        Log out of your account
                      </div>
                    </div>
                    <svg
                      className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${
                        darkMode ? "text-white/30" : "text-gray-400"
                      } group-hover:scale-110 transition-transform duration-200`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Main Content - Modern Responsive Layout */}
      <div className="flex min-h-[calc(100vh-4rem)] overflow-hidden">
        <div className="w-full p-4 sm:p-6 lg:p-8 flex items-center justify-center">
          <div className="w-full max-w-lg flex flex-col items-center justify-center">
            {/* Meeting Info Card */}
            <div
              className={`w-full max-w-md mb-8 sm:mb-10 p-6 sm:p-8 rounded-2xl ${
                darkMode
                  ? "bg-white/5 backdrop-blur-xl border border-white/10"
                  : "bg-white/80 backdrop-blur-xl border border-gray-200/50"
              } shadow-xl`}
            >
              {/* Profile Picture - Inside Card */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full p-1 bg-gradient-to-r from-pink-500 to-red-500 shadow-xl">
                    <div className="w-full h-full rounded-full overflow-hidden">
                      <img
                        src="https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"
                        alt="User Profile"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Title */}
              <div className="text-center mb-6">
                <h2
                  className={`text-xl sm:text-2xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  {meetingName}
                </h2>
              </div>

              {/* Name Input - Enhanced */}
              <div className="mb-6">
                <label
                  htmlFor="name"
                  className={`block text-sm font-semibold mb-3 ${
                    darkMode ? "text-white/80" : "text-gray-700"
                  }`}
                >
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className={`w-full px-4 py-4 border-2 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200 text-base ${
                    darkMode
                      ? "bg-white/5 border-white/10 text-white placeholder-white/40 hover:border-white/20"
                      : "bg-white border-gray-200 text-gray-900 placeholder-gray-500 hover:border-gray-300"
                  }`}
                  onKeyPress={(e) => e.key === "Enter" && handleJoinMeeting()}
                />
              </div>

              {/* Camera and Microphone Controls - Enhanced */}
              <div className="flex gap-4 mb-8 justify-center">
                {/* Microphone Button */}
                <div className="relative">
                  <button
                    onClick={() => setMicrophoneEnabled(!microphoneEnabled)}
                    className={`p-4 sm:p-5 rounded-full transition-all duration-200 relative group ${
                      !microphoneEnabled
                        ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-xl"
                        : darkMode
                        ? "bg-white/10 text-white hover:bg-white/20 hover:shadow-lg"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-lg"
                    }`}
                  >
                    {microphoneEnabled ? (
                      <Mic size={24} />
                    ) : (
                      <MicOff size={24} />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowMicDropdown(!showMicDropdown);
                      }}
                      className={`absolute -top-1 -right-1 p-1.5 rounded-full transition-all duration-200 ${
                        darkMode
                          ? "bg-gray-800/80 text-white hover:bg-gray-700/80"
                          : "bg-white/90 text-gray-600 hover:bg-gray-100/90"
                      } shadow-sm`}
                    >
                      <ChevronUp
                        size={12}
                        className={`transition-transform duration-200 ${
                          showMicDropdown ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </button>

                  {/* Audio Settings Dropdown */}
                  {showMicDropdown && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 w-56 rounded-xl shadow-2xl backdrop-blur-xl z-[9999]">
                      <div
                        className={`${
                          darkMode
                            ? "bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 border border-white/10"
                            : "bg-gradient-to-br from-white/95 via-gray-50/95 to-white/95 border border-gray-200/50"
                        } rounded-xl px-2 py-3 shadow-xl`}
                      >
                        {/* Microphone Section */}
                        <div className="mb-2 pt-1">
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <div
                              className={`p-0.5 rounded ${
                                darkMode ? "bg-white/5" : "bg-gray-100"
                              }`}
                            >
                              <Mic
                                size={10}
                                className={
                                  darkMode ? "text-white/70" : "text-gray-600"
                                }
                              />
                            </div>
                            <h4
                              className={`text-xs font-semibold uppercase tracking-wider ${
                                darkMode ? "text-white/50" : "text-gray-400"
                              }`}
                            >
                              Microphone
                            </h4>
                          </div>
                          <div className="space-y-0.5">
                            {mockAudioDevices.microphones.map((mic) => (
                              <button
                                key={mic.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMicrophoneSelect(mic.name);
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                                tabIndex={-1}
                                type="button"
                                className={`w-full text-left p-1.5 rounded-lg transition-all duration-200 focus:outline-none ${
                                  selectedMicrophone === mic.name
                                    ? `${
                                        darkMode
                                          ? "bg-gradient-to-r from-pink-500 to-red-500 text-white"
                                          : "bg-gradient-to-r from-pink-500 to-red-500 text-white"
                                      }`
                                    : `${
                                        darkMode
                                          ? "text-white/80"
                                          : "text-gray-700"
                                      }`
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium truncate">
                                    {mic.name}
                                  </span>
                                  {selectedMicrophone === mic.name && (
                                    <div
                                      className={`p-0.5 rounded-full ${
                                        darkMode
                                          ? "bg-pink-500/20"
                                          : "bg-pink-100"
                                      }`}
                                    >
                                      <CheckCircle
                                        size={10}
                                        className="text-white"
                                      />
                                    </div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Speaker Section */}
                        <div>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <div
                              className={`p-0.5 rounded ${
                                darkMode ? "bg-white/5" : "bg-gray-100"
                              }`}
                            >
                              <Volume2
                                size={10}
                                className={
                                  darkMode ? "text-white/70" : "text-gray-600"
                                }
                              />
                            </div>
                            <h4
                              className={`text-xs font-semibold uppercase tracking-wider ${
                                darkMode ? "text-white/50" : "text-gray-400"
                              }`}
                            >
                              Speaker
                            </h4>
                          </div>
                          <div className="space-y-0.5">
                            {mockAudioDevices.speakers.map((speaker) => (
                              <button
                                key={speaker.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSpeakerSelect(speaker.name);
                                }}
                                onMouseDown={(e) => e.preventDefault()}
                                tabIndex={-1}
                                type="button"
                                className={`w-full text-left p-1.5 rounded-lg transition-all duration-200 focus:outline-none ${
                                  selectedSpeaker === speaker.name
                                    ? `${
                                        darkMode
                                          ? "bg-gradient-to-r from-pink-500 to-red-500 text-white"
                                          : "bg-gradient-to-r from-pink-500 to-red-500 text-white"
                                      }`
                                    : `${
                                        darkMode
                                          ? "text-white/80"
                                          : "text-gray-700"
                                      }`
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-medium truncate">
                                    {speaker.name}
                                  </span>
                                  {selectedSpeaker === speaker.name && (
                                    <div
                                      className={`p-0.5 rounded-full ${
                                        darkMode
                                          ? "bg-pink-500/20"
                                          : "bg-pink-100"
                                      }`}
                                    >
                                      <CheckCircle
                                        size={10}
                                        className="text-white"
                                      />
                                    </div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Camera Button */}
                <div className="relative">
                  <button
                    onClick={() => setCameraEnabled(!cameraEnabled)}
                    className={`p-4 sm:p-5 rounded-full transition-all duration-200 relative group ${
                      !cameraEnabled
                        ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-xl"
                        : darkMode
                        ? "bg-white/10 text-white hover:bg-white/20 hover:shadow-lg"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-lg"
                    }`}
                  >
                    {cameraEnabled ? (
                      <Video size={24} />
                    ) : (
                      <VideoOff size={24} />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowCameraDropdown(!showCameraDropdown);
                      }}
                      className={`absolute -top-1 -right-1 p-1.5 rounded-full transition-all duration-200 ${
                        darkMode
                          ? "bg-gray-800/80 text-white hover:bg-gray-700/80"
                          : "bg-white/90 text-gray-600 hover:bg-gray-100/90"
                      } shadow-sm`}
                    >
                      <ChevronUp
                        size={12}
                        className={`transition-transform duration-200 ${
                          showCameraDropdown ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </button>

                  {/* Camera Settings Dropdown */}
                  {showCameraDropdown && (
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 w-64 rounded-xl shadow-2xl backdrop-blur-xl z-[9999]">
                      <div
                        className={`${
                          darkMode
                            ? "bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 border border-white/10"
                            : "bg-gradient-to-br from-white/95 via-gray-50/95 to-white/95 border border-gray-200/50"
                        } rounded-xl px-2 py-3 shadow-xl`}
                      >
                        {/* Preview */}
                        <div
                          className={`rounded-lg overflow-hidden mb-2 ${
                            darkMode ? "bg-gray-800" : "bg-gray-200"
                          } aspect-video flex items-center justify-center w-11/12 sm:w-3/4 lg:w-3/5 mx-auto`}
                        >
                          <div className="w-full h-full flex items-center justify-center">
                            <div
                              className={`w-20 h-20 rounded-full flex items-center justify-center ${
                                darkMode ? "bg-gray-700" : "bg-gray-300"
                              }`}
                            >
                              <Camera
                                size={24}
                                className={
                                  darkMode ? "text-white/60" : "text-gray-600"
                                }
                              />
                            </div>
                          </div>
                        </div>

                        {/* Camera List */}
                        <div className="space-y-0.5">
                          {mockAudioDevices.cameras.map((cam) => (
                            <button
                              key={cam.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCameraSelect(cam.name);
                              }}
                              onMouseDown={(e) => e.preventDefault()}
                              tabIndex={-1}
                              type="button"
                              className={`w-full text-left p-1.5 rounded-lg transition-all duration-200 focus:outline-none ${
                                selectedCamera === cam.name
                                  ? `${
                                      darkMode
                                        ? "bg-gradient-to-r from-pink-500 to-red-500 text-white"
                                        : "bg-gradient-to-r from-pink-500 to-red-500 text-white"
                                    }`
                                  : `${
                                      darkMode
                                        ? "text-white/80"
                                        : "text-gray-700"
                                    }`
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium truncate">
                                  {cam.name}
                                </span>
                                {selectedCamera === cam.name && (
                                  <div
                                    className={`p-0.5 rounded-full ${
                                      darkMode
                                        ? "bg-pink-500/20"
                                        : "bg-pink-100"
                                    }`}
                                  >
                                    <CheckCircle
                                      size={10}
                                      className="text-white"
                                    />
                                  </div>
                                )}
                              </div>
                            </button>
                          ))}
                        </div>

                        {/* Mirror my video */}
                        <div
                          className={`mt-4 pt-3 pb-2 border-t ${
                            darkMode ? "border-white/10" : "border-gray-200/70"
                          }`}
                        >
                          <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={mirrorVideo}
                              onChange={(e) => setMirrorVideo(e.target.checked)}
                              className="h-3.5 w-3.5 rounded border-gray-300 text-pink-500 focus:ring-0 focus:outline-none"
                            />
                            <span
                              className={
                                darkMode ? "text-white/80" : "text-gray-700"
                              }
                            >
                              Mirror my video
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Settings Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowSettingsModal(true)}
                    className={`p-4 sm:p-5 rounded-full transition-all duration-200 group ${
                      darkMode
                        ? "bg-white/10 text-white hover:bg-white/20 hover:shadow-lg"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-lg"
                    }`}
                    aria-label="Settings"
                    title="Settings"
                  >
                    <Settings size={24} />
                  </button>
                </div>
              </div>

              {/* Join Meeting Button - Enhanced */}
              <button
                onClick={handleJoinMeeting}
                disabled={!name.trim()}
                className={`w-full py-4 px-6 rounded-xl font-semibold text-base transition-all duration-300 ${
                  name.trim()
                    ? "bg-gradient-to-r from-pink-500 to-red-500 text-white hover:from-pink-600 hover:to-red-600 transform hover:scale-105 shadow-xl hover:shadow-2xl"
                    : darkMode
                    ? "bg-gray-600/50 text-gray-400 cursor-not-allowed"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Join Meeting
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setShowSettingsModal(false)}
          />
          <div
            className={`${
              darkMode
                ? "bg-gradient-to-br from-[#12121d] to-[#1e1e2f] border border-white/10"
                : "bg-white border border-gray-200/60"
            } relative w-full max-w-4xl rounded-2xl overflow-hidden`}
          >
            <button
              onClick={() => setShowSettingsModal(false)}
              className={`absolute top-3 right-3 p-2 rounded-lg ${
                darkMode
                  ? "hover:bg-white/10 text-white/70 hover:text-white"
                  : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
              }`}
              aria-label="Close"
              title="Close"
            >
              <X size={18} />
            </button>
            <div className="grid grid-cols-[220px_1fr] min-h-[520px]">
              {/* Sidebar Tabs */}
              <div
                className={`${
                  darkMode
                    ? "bg-white/5 border-r border-white/10"
                    : "bg-gray-50 border-r border-gray-200"
                } p-4`}
              >
                {(
                  [
                    "Audio",
                    "Video",
                    "Notifications",
                    "Profile",
                    "Shortcuts",
                    "General",
                  ] as const
                ).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setSettingsTab(tab)}
                    className={`w-full text-left px-3 py-2 rounded-lg mb-2 text-sm font-medium transition-colors ${
                      settingsTab === tab
                        ? darkMode
                          ? "bg-gradient-to-r from-pink-500 to-red-500 text-white"
                          : "bg-gradient-to-r from-pink-500 to-red-500 text-white"
                        : darkMode
                        ? "hover:bg-white/10 text-white/80"
                        : "hover:bg-gray-100 text-gray-800"
                    }`}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              {/* Content */}
              <div className="p-6 space-y-4">
                {settingsTab === "Audio" && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Audio</h3>
                      <p
                        className={`${
                          darkMode ? "text-white/60" : "text-gray-600"
                        } text-sm`}
                      >
                        Configure microphone, speaker, and advanced audio
                        options.
                      </p>
                    </div>

                    {/* Microphone Section */}
                    <div className="space-y-4">
                      <div>
                        <h4
                          className={`text-sm font-semibold mb-2 ${
                            darkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          Microphone
                        </h4>
                        <div className="space-y-3">
                          {/* Microphone Dropdown */}
                          <div className="relative">
                            <select
                              value={selectedMicrophone}
                              onChange={(e) =>
                                handleMicrophoneSelect(e.target.value)
                              }
                              className={`w-full px-3 py-2 rounded-lg text-sm outline-none border transition-all duration-200 focus:ring-2 focus:ring-pink-500/50 focus:border-transparent ${
                                darkMode
                                  ? "bg-white/5 border-white/10 text-white"
                                  : "bg-gray-50/80 border-gray-200 text-gray-900"
                              }`}
                            >
                              {mockAudioDevices.microphones.map((mic) => (
                                <option
                                  key={mic.id}
                                  value={mic.name}
                                  className={
                                    darkMode
                                      ? "bg-gray-800 text-white"
                                      : "bg-white text-gray-900"
                                  }
                                >
                                  {mic.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Live Audio Test Bar */}
                          <div className="space-y-2">
                            <div
                              className={`text-xs ${
                                darkMode ? "text-white/70" : "text-gray-600"
                              }`}
                            >
                              Live audio test
                            </div>
                            <div
                              className={`w-full h-2 rounded-full ${
                                darkMode ? "bg-white/10" : "bg-gray-200"
                              }`}
                            >
                              <div
                                className="h-full rounded-full bg-gradient-to-r from-pink-500 to-red-500 transition-all duration-300"
                                style={{ width: `${Math.random() * 60 + 20}%` }}
                              ></div>
                            </div>
                            <div
                              className={`text-xs ${
                                darkMode ? "text-white/50" : "text-gray-500"
                              }`}
                            >
                              Audio level: {Math.floor(Math.random() * 40 + 30)}
                              %
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Audio Output Section */}
                      <div>
                        <h4
                          className={`text-sm font-semibold mb-2 ${
                            darkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          Audio Output
                        </h4>
                        <div className="space-y-3">
                          {/* Speaker Dropdown */}
                          <div className="relative">
                            <select
                              value={selectedSpeaker}
                              onChange={(e) =>
                                handleSpeakerSelect(e.target.value)
                              }
                              className={`w-full px-3 py-2 rounded-lg text-sm outline-none border transition-all duration-200 focus:ring-2 focus:ring-pink-500/50 focus:border-transparent ${
                                darkMode
                                  ? "bg-white/5 border-white/10 text-white"
                                  : "bg-gray-50/80 border-gray-200 text-gray-900"
                              }`}
                            >
                              {mockAudioDevices.speakers.map((speaker) => (
                                <option
                                  key={speaker.id}
                                  value={speaker.name}
                                  className={
                                    darkMode
                                      ? "bg-gray-800 text-white"
                                      : "bg-white text-gray-900"
                                  }
                                >
                                  {speaker.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Test Button */}
                          <button
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                              darkMode
                                ? "bg-white/10 hover:bg-white/20 text-white"
                                : "bg-gray-100 hover:bg-gray-200 text-gray-900"
                            }`}
                          >
                            Test Audio Output
                          </button>
                        </div>
                      </div>

                      {/* Noise Suppression */}
                      <div>
                        <h4
                          className={`text-sm font-semibold mb-2 ${
                            darkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          Advanced
                        </h4>
                        <label className="flex items-center gap-3 text-sm cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={enableNoiseSuppression}
                            onChange={(e) =>
                              setEnableNoiseSuppression(e.target.checked)
                            }
                            className="h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-0 focus:outline-none"
                          />
                          <span
                            className={
                              darkMode ? "text-white/90" : "text-gray-900"
                            }
                          >
                            Noise suppression
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                )}
                {settingsTab === "Video" && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Video</h3>
                      <p
                        className={`${
                          darkMode ? "text-white/60" : "text-gray-600"
                        } text-sm`}
                      >
                        Select camera, resolution, and layout preferences.
                      </p>
                    </div>

                    {/* Camera Preview */}
                    <div>
                      <h4
                        className={`text-sm font-semibold mb-2 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Camera Preview
                      </h4>
                      <div
                        className={`rounded-lg overflow-hidden ${
                          darkMode ? "bg-gray-800" : "bg-gray-200"
                        } h-48 flex items-center justify-center`}
                      >
                        <div className="w-full h-full flex items-center justify-center">
                          <div
                            className={`w-20 h-20 rounded-full flex items-center justify-center ${
                              darkMode ? "bg-gray-700" : "bg-gray-300"
                            }`}
                          >
                            <Camera
                              size={24}
                              className={
                                darkMode ? "text-white/60" : "text-gray-600"
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Camera Settings */}
                    <div className="space-y-4">
                      <div>
                        <h4
                          className={`text-sm font-semibold mb-2 ${
                            darkMode ? "text-white" : "text-gray-900"
                          }`}
                        >
                          Camera
                        </h4>
                        <div className="space-y-3">
                          {/* Camera Dropdown */}
                          <div className="relative">
                            <select
                              value={selectedCamera}
                              onChange={(e) =>
                                handleCameraSelect(e.target.value)
                              }
                              className={`w-full px-3 py-2 rounded-lg text-sm outline-none border transition-all duration-200 focus:ring-2 focus:ring-pink-500/50 focus:border-transparent ${
                                darkMode
                                  ? "bg-white/5 border-white/10 text-white"
                                  : "bg-gray-50/80 border-gray-200 text-gray-900"
                              }`}
                            >
                              {mockAudioDevices.cameras.map((cam) => (
                                <option
                                  key={cam.id}
                                  value={cam.name}
                                  className={
                                    darkMode
                                      ? "bg-gray-800 text-white"
                                      : "bg-white text-gray-900"
                                  }
                                >
                                  {cam.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Mirror Video Checkbox */}
                          <label className="flex items-center gap-3 text-sm cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={mirrorVideo}
                              onChange={(e) => setMirrorVideo(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-0 focus:outline-none"
                            />
                            <span
                              className={
                                darkMode ? "text-white/90" : "text-gray-900"
                              }
                            >
                              Mirror my video
                            </span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {settingsTab === "Notifications" && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">
                        Notifications
                      </h3>
                      <p
                        className={`${
                          darkMode ? "text-white/60" : "text-gray-600"
                        } text-sm`}
                      >
                        Control system notifications and sounds.
                      </p>
                    </div>

                    {/* Play Sound On Section */}
                    <div>
                      <h4
                        className={`text-sm font-semibold mb-3 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Play sound on:
                      </h4>
                      <div className="space-y-3">
                        {[
                          "Meeting Reactions",
                          "Incoming Message",
                          "Participant Joined",
                          "Participant Left",
                          "Talk While Muted",
                          "Participant Entered Lobby",
                        ].map((notification) => (
                          <label
                            key={notification}
                            className="flex items-center gap-3 text-sm cursor-pointer select-none"
                          >
                            <input
                              type="checkbox"
                              defaultChecked={true}
                              className="h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-0 focus:outline-none"
                            />
                            <span
                              className={
                                darkMode ? "text-white/90" : "text-gray-900"
                              }
                            >
                              {notification}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                {settingsTab === "Profile" && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Profile</h3>
                      <p
                        className={`${
                          darkMode ? "text-white/60" : "text-gray-600"
                        } text-sm`}
                      >
                        Update your profile details and preferences.
                      </p>
                    </div>

                    {/* Profile Image */}
                    <div>
                      <h4
                        className={`text-sm font-semibold mb-3 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Profile Image
                      </h4>
                      <div className="flex items-center gap-4">
                        <div className="relative">
                          <img
                            src="https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"
                            alt="Profile"
                            className="w-16 h-16 rounded-full border-2 border-gray-200"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Name */}
                    <div>
                      <h4
                        className={`text-sm font-semibold mb-2 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Name
                      </h4>
                      <input
                        type="text"
                        defaultValue="Ledy Sintia"
                        className={`w-full px-3 py-2 rounded-lg text-sm outline-none border transition-all duration-200 focus:ring-2 focus:ring-pink-500/50 focus:border-transparent ${
                          darkMode
                            ? "bg-white/5 border-white/10 text-white placeholder-white/40"
                            : "bg-gray-50/80 border-gray-200 text-gray-900 placeholder-gray-500"
                        }`}
                        placeholder="Enter your name"
                      />
                    </div>

                    {/* Logged In As */}
                    <div>
                      <h4
                        className={`text-sm font-semibold mb-2 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Logged in as
                      </h4>
                      <div
                        className={`px-3 py-2 rounded-lg text-sm ${
                          darkMode
                            ? "bg-white/5 text-white/70"
                            : "bg-gray-50/80 text-gray-600"
                        }`}
                      >
                        ledy.sintia@email.com
                      </div>
                    </div>
                  </div>
                )}
                {settingsTab === "Shortcuts" && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">Shortcuts</h3>
                      <p
                        className={`${
                          darkMode ? "text-white/60" : "text-gray-600"
                        } text-sm`}
                      >
                        Configure keyboard shortcuts for quick actions.
                      </p>
                    </div>

                    {/* Enable Keyboard Shortcuts */}
                    <div>
                      <label className="flex items-center gap-3 text-sm cursor-pointer select-none">
                        <input
                          type="checkbox"
                          defaultChecked={true}
                          className="h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-0 focus:outline-none"
                        />
                        <span
                          className={
                            darkMode ? "text-white/90" : "text-gray-900"
                          }
                        >
                          Enable keyboard shortcuts
                        </span>
                      </label>
                    </div>

                    {/* Shortcuts List */}
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
                      {[
                        { action: "Show or hide video thumbnails", key: "F" },
                        { action: "Mute or unmute your microphone", key: "M" },
                        { action: "Start or stop camera", key: "V" },
                        { action: "Manage video quality", key: "A" },
                        { action: "Open or close the chat", key: "C" },
                        {
                          action: "Switch between camera and screen sharing",
                          key: "D",
                        },
                        {
                          action: "Show or hide the participants pane",
                          key: "P",
                        },
                        { action: "Raise or lower your hand", key: "R" },
                        { action: "View or exit full screen", key: "S" },
                        { action: "Toggle tile view", key: "W" },
                        { action: "Show participants stats", key: "T" },
                        { action: "Send thumbs up reaction", key: "⌥ + T" },
                        { action: "Send clap reaction", key: "⌥ + C" },
                        { action: "Send laugh reaction", key: "⌥ + L" },
                        { action: "Send surprised reaction", key: "⌥ + O" },
                        { action: "Send boo reaction", key: "⌥ + B" },
                        { action: "Send silence reaction", key: "⌥ + S" },
                        { action: "Show or hide keyboard shortcuts", key: "?" },
                        { action: "Push to talk", key: "Space" },
                        { action: "Focus on your video", key: "0" },
                        {
                          action: "Focus on another person's video",
                          key: "1-9",
                        },
                      ].map((shortcut, index) => (
                        <div
                          key={index}
                          className={`flex items-center justify-between p-3 rounded-lg ${
                            darkMode ? "bg-white/5" : "bg-gray-50/80"
                          }`}
                        >
                          <span
                            className={`text-sm ${
                              darkMode ? "text-white/90" : "text-gray-900"
                            }`}
                          >
                            {shortcut.action}
                          </span>
                          <div
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              darkMode
                                ? "bg-white/10 text-white/80"
                                : "bg-gray-200 text-gray-700"
                            }`}
                          >
                            {shortcut.key}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {settingsTab === "General" && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold mb-3">General</h3>
                      <p
                        className={`${
                          darkMode ? "text-white/60" : "text-gray-600"
                        } text-sm`}
                      >
                        General application settings.
                      </p>
                    </div>

                    {/* Enable Pre-meeting Screen */}
                    <div>
                      <label className="flex items-center gap-3 text-sm cursor-pointer select-none">
                        <input
                          type="checkbox"
                          defaultChecked={true}
                          className="h-4 w-4 rounded border-gray-300 text-pink-500 focus:ring-0 focus:outline-none"
                        />
                        <span
                          className={
                            darkMode ? "text-white/90" : "text-gray-900"
                          }
                        >
                          Enable pre-meeting screen
                        </span>
                      </label>
                    </div>

                    {/* Language */}
                    <div>
                      <h4
                        className={`text-sm font-semibold mb-2 ${
                          darkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        Language
                      </h4>
                      <div className="relative">
                        <select
                          className={`w-full px-3 py-2 rounded-lg text-sm outline-none border transition-all duration-200 focus:ring-2 focus:ring-pink-500/50 focus:border-transparent ${
                            darkMode
                              ? "bg-white/5 border-white/10 text-white"
                              : "bg-gray-50/80 border-gray-200 text-gray-900"
                          }`}
                        >
                          <option
                            value="en"
                            className={
                              darkMode
                                ? "bg-gray-800 text-white"
                                : "bg-white text-gray-900"
                            }
                          >
                            English
                          </option>
                          <option
                            value="es"
                            className={
                              darkMode
                                ? "bg-gray-800 text-white"
                                : "bg-white text-gray-900"
                            }
                          >
                            Español
                          </option>
                          <option
                            value="fr"
                            className={
                              darkMode
                                ? "bg-gray-800 text-white"
                                : "bg-white text-gray-900"
                            }
                          >
                            Français
                          </option>
                          <option
                            value="de"
                            className={
                              darkMode
                                ? "bg-gray-800 text-white"
                                : "bg-white text-gray-900"
                            }
                          >
                            Deutsch
                          </option>
                          <option
                            value="it"
                            className={
                              darkMode
                                ? "bg-gray-800 text-white"
                                : "bg-white text-gray-900"
                            }
                          >
                            Italiano
                          </option>
                          <option
                            value="pt"
                            className={
                              darkMode
                                ? "bg-gray-800 text-white"
                                : "bg-white text-gray-900"
                            }
                          >
                            Português
                          </option>
                          <option
                            value="ru"
                            className={
                              darkMode
                                ? "bg-gray-800 text-white"
                                : "bg-white text-gray-900"
                            }
                          >
                            Русский
                          </option>
                          <option
                            value="zh"
                            className={
                              darkMode
                                ? "bg-gray-800 text-white"
                                : "bg-white text-gray-900"
                            }
                          >
                            中文
                          </option>
                          <option
                            value="ja"
                            className={
                              darkMode
                                ? "bg-gray-800 text-white"
                                : "bg-white text-gray-900"
                            }
                          >
                            日本語
                          </option>
                          <option
                            value="ko"
                            className={
                              darkMode
                                ? "bg-gray-800 text-white"
                                : "bg-white text-gray-900"
                            }
                          >
                            한국어
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Modal */}
      {showNotificationsModal && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm ${
            darkMode ? "bg-black/60" : "bg-gray-900/40"
          }`}
        >
          <div
            className={`rounded-2xl shadow-2xl p-8 md:p-12 w-[90vw] max-w-4xl relative max-h-[90vh] overflow-y-auto scrollbar-hide ${
              darkMode ? "bg-[#232336]" : "bg-white"
            }`}
          >
            <button
              className={`absolute top-6 right-6 transition-colors duration-200 ${
                darkMode
                  ? "text-white/60 hover:text-white"
                  : "text-gray-600 hover:text-gray-900"
              }`}
              onClick={() => setShowNotificationsModal(false)}
            >
              <X size={28} />
            </button>
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-1 h-6 bg-gradient-to-b from-pink-600 to-red-500 rounded-full"></div>
                <h2
                  className={`text-3xl font-bold ${
                    darkMode ? "text-white" : "text-gray-900"
                  }`}
                >
                  Notifications
                </h2>
              </div>
            </div>
            {/* Tab Navigation */}
            <div
              className={`flex gap-1 mb-8 rounded-xl p-1 ${
                darkMode ? "bg-white/5" : "bg-gray-100"
              }`}
            >
              <button
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  notificationsTab === "current"
                    ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
                    : darkMode
                    ? "text-white/60 hover:text-white hover:bg-white/5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                }`}
                onClick={() => setNotificationsTab("current")}
              >
                Current ({notifications.length})
              </button>
              <button
                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  notificationsTab === "cleared"
                    ? "bg-gradient-to-r from-pink-500 to-red-500 text-white shadow-lg"
                    : darkMode
                    ? "text-white/60 hover:text-white hover:bg-white/5"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-200"
                }`}
                onClick={() => setNotificationsTab("cleared")}
              >
                Cleared ({clearedNotifications.length})
              </button>
            </div>
            {notificationsTab === "current" && (
              <div className="space-y-6">
                {notifications.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full ${
                        darkMode ? "bg-white/5" : "bg-gray-100"
                      }`}
                    >
                      <Bell
                        size={32}
                        className={`${
                          darkMode ? "text-white/40" : "text-gray-400"
                        }`}
                      />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      No notifications
                    </h3>
                    <p
                      className={`${
                        darkMode ? "text-white/60" : "text-gray-600"
                      }`}
                    >
                      You're all caught up! Check back later for updates.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4">
                      {notifications.map((n) => (
                        <div
                          key={n.id}
                          className={`group rounded-xl p-6 border transition-all duration-200 ${
                            darkMode
                              ? "bg-white/5 border-white/10 hover:border-white/20"
                              : "bg-gray-50 border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 mt-1">{n.icon}</div>
                            <div className="flex-1 min-w-0">
                              <p
                                className={`text-base leading-relaxed mb-2 ${
                                  darkMode ? "text-white/90" : "text-gray-900"
                                }`}
                              >
                                {n.text}
                              </p>
                              <div
                                className={`flex items-center gap-4 text-sm ${
                                  darkMode ? "text-white/40" : "text-gray-500"
                                }`}
                              >
                                <span>Just now</span>
                                <span>•</span>
                                <span>Unread</span>
                              </div>
                            </div>
                            <button
                              className={`flex-shrink-0 p-2 rounded-lg transition-all duration-200 group-hover:opacity-100 opacity-0 ${
                                darkMode
                                  ? "hover:bg-red-600/20 text-white/60 hover:text-red-400"
                                  : "hover:bg-red-50 text-gray-600 hover:text-red-600"
                              }`}
                              onClick={() => handleClearNotification(n.id)}
                              aria-label="Clear notification"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      className={`flex justify-center pt-6 border-t ${
                        darkMode ? "border-white/10" : "border-gray-200"
                      }`}
                    >
                      <button
                        className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:scale-105 text-white font-semibold py-3 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleClearAll}
                        disabled={notifications.length === 0}
                      >
                        Clear All Notifications
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
            {notificationsTab === "cleared" && (
              <div className="space-y-6">
                {clearedNotifications.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full ${
                        darkMode ? "bg-white/5" : "bg-gray-100"
                      }`}
                    >
                      <Trash2
                        size={32}
                        className={`${
                          darkMode ? "text-white/40" : "text-gray-400"
                        }`}
                      />
                    </div>
                    <h3
                      className={`text-xl font-semibold mb-2 ${
                        darkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      No cleared notifications
                    </h3>
                    <p
                      className={`${
                        darkMode ? "text-white/60" : "text-gray-600"
                      }`}
                    >
                      Notifications you clear will appear here.
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="grid gap-4">
                      {clearedNotifications.map((n) => (
                        <div
                          key={n.id}
                          className={`group rounded-xl p-6 border transition-all duration-200 ${
                            darkMode
                              ? "bg-white/5 border-white/10 hover:border-white/20"
                              : "bg-gray-50 border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0 mt-1">{n.icon}</div>
                            <div className="flex-1 min-w-0">
                              <p
                                className={`text-base leading-relaxed mb-2 line-through ${
                                  darkMode ? "text-white/70" : "text-gray-600"
                                }`}
                              >
                                {n.text}
                              </p>
                              <div
                                className={`flex items-center gap-4 text-sm ${
                                  darkMode ? "text-white/30" : "text-gray-400"
                                }`}
                              >
                                <span>Cleared</span>
                                <span>•</span>
                                <span>Just now</span>
                              </div>
                            </div>
                            <button
                              className={`flex-shrink-0 p-2 rounded-lg transition-all duration-200 group-hover:opacity-100 opacity-0 ${
                                darkMode
                                  ? "hover:bg-green-600/20 text-white/60 hover:text-green-400"
                                  : "hover:bg-green-200 text-green-700 hover:text-green-900 bg-green-50"
                              }`}
                              onClick={() => handleRestoreNotification(n.id)}
                              aria-label="Restore notification"
                            >
                              <Plus size={18} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PreMeetingRoom;
